import React from 'react';

import { DefaultContext } from '@apollo/client';

import { EMPLOYERS_DEFAULT_PIN_SETTINGS_FORM_VALUES } from '@clearverify/constants';

import { PeoInfoFragment, PeoUpdateWithEmployersMutationVariables } from 'src/api';
import { useNotification } from 'src/hooks/app';
import { RequestAutoAccept } from 'src/types/settingsForm';

import { EmployeeSettingsForm } from '../EmployeeSettingsForm';
import { SettingsFormFields, SettingsFormValues } from '../EmployeeSettingsForm/types';

interface PeoSettingsFormProps {
  peo: PeoInfoFragment;
  updatePeoWithEmployers: (
    data: Omit<PeoUpdateWithEmployersMutationVariables, 'id'>,
    context?: DefaultContext,
  ) => Promise<void>;
}

export const PeoSettingsForm: React.FC<PeoSettingsFormProps> = ({
  peo,
  updatePeoWithEmployers,
}) => {
  const { spawnNotification } = useNotification();

  const { signUpMethod } = peo;

  const onSubmit = React.useCallback(
    async (data: SettingsFormValues) => {
      try {
        const requestAutoAccept =
          data.isAutoVerificationRequestEnabled === RequestAutoAccept.REQUIRED;
        const { signUpMethod } = data;

        await updatePeoWithEmployers({
          data: {
            requestAutoAccept,
            signUpMethod,
            ...EMPLOYERS_DEFAULT_PIN_SETTINGS_FORM_VALUES,
          },
        });

        spawnNotification('Changes are saved successfully', 'success');
      } catch (error) {
        if (error instanceof Error) {
          console.error(error);
          spawnNotification(error.message, 'error');
        } else {
          console.error(error);
        }
      }
    },
    [updatePeoWithEmployers, spawnNotification],
  );

  const initialValues: SettingsFormValues = React.useMemo(
    () => ({
      [SettingsFormFields.signUpMethod]: signUpMethod,
    }),
    [signUpMethod],
  );

  return <EmployeeSettingsForm onSubmit={onSubmit} initialValues={initialValues} />;
};
